import { Component, OnDestroy, OnInit } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { DataService } from 'src/app/services/data.service';
import { RadioComponent } from '../../shared/components/radio/radio.component';
import {
  FORBIDDEN_PHONES,
  forbiddenPhoneValidator,
} from 'src/app/shared/constants/phone-validator';
import { Observable, Subscription, map, of } from 'rxjs';
import { EventData, FormData, Property } from 'src/app/services/data.model';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { VALIDATION_PATTERNS } from 'src/app/shared/constants/text-field';
import { NavigationService } from 'src/app/services/navigation.service';
import { HOMEOWNER_INFO_MAP } from './homeowner.const';

@Component({
  selector: 'app-homeowner',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MainLayoutComponent,
    FormGroupComponent,
    RadioComponent
],
  templateUrl: './homeowner.component.html',
  styleUrl: './homeowner.component.scss',
})
export class HomeownerComponent implements OnInit, OnDestroy {
  homeownerForm: FormGroup;
  radioGroup = [];
  homeownersList: Array<FormGroup> = [];
  previouslyFilledData: Property;
  subscriptions: Subscription = new Subscription();
  windowCount: number = 0;
  defaultOtherId: number = 876090099;
  showOtherBlock = false;
  otherTextfieldMaxLength = 125;
  propertyTypeRadioGroup = [];
  defaultPropertyType = 876090000;
  isEvent = false;
  HOMEOWNER_INFO_MAP = HOMEOWNER_INFO_MAP;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    public nextButtonState: NextButtonStateService,
    private navigationService: NavigationService
  ) {
    this.checkEventFlow();
  }

  ngOnInit(): void {
    this.buildForm();
    if (this.dataService.id) {
      const sub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          this.prepopulateStepData(data);
        });
      this.subscriptions.add(sub);
    }
  }

  buildForm() {
    this.homeownerForm = this.fb.group({
      onlyHomeowner: ['', Validators.required],
      propertyType: ['', Validators.required],
      propertyTypeOtherText: [''],
    });
    this.homeownerForm.controls['onlyHomeowner'].valueChanges.subscribe(
      (value) => {
        if (
          value ===
            HOMEOWNER_INFO_MAP.homeownerFormRadioButtons[1].unique_name &&
          !this.homeownersList.length
        ) {
          this.homeownersList.push(
            this.fb.group({
              firstName: [
                '',
                [
                  Validators.required,
                  Validators.maxLength(50),
                  Validators.pattern(VALIDATION_PATTERNS.text),
                ],
              ],
              lastName: [
                '',
                [
                  Validators.required,
                  Validators.maxLength(50),
                  Validators.pattern(VALIDATION_PATTERNS.text),
                ],
              ],
              phone: [
                '',
                [
                  Validators.pattern('^[0-9]{10}$'),
                  forbiddenPhoneValidator(FORBIDDEN_PHONES),
                ],
              ],
            })
          );
        }
        if (this.homeownerForm && value === 'multiple_homeowners') {
          this.trackHomeownersValidation();
        }
        this.checkFormValidity();
      }
    );
    this.subscribeToFormChanges();
    this.updateFormValues();
  }

  checkEventFlow() {
    const eventSub = this.navigationService
      .getEventData()
      .subscribe((event: EventData) => {
        this.isEvent = !!event?.isEvent;
      });
    this.subscriptions.add(eventSub);
  }

  subscribeToFormChanges(): void {
    const formChangesSub = this.homeownerForm.valueChanges.subscribe(
      (value) => {
        if (value.propertyType !== this.defaultOtherId && this.showOtherBlock) {
          this.switchOther(false);
        }
        this.checkFormValidity();
      }
    );
    this.subscriptions.add(formChangesSub);
  }

  switchOther(show, e?) {
    if (e) e.preventDefault();
    this.showOtherBlock = show;
    this.addOtherControlValidation(show);
    this.checkFormValidity();
  }

  addOtherControlValidation(add: boolean) {
    const other = this.homeownerForm.get('propertyTypeOtherText');
    if (add) {
      this.homeownerForm.patchValue({ propertyType: this.defaultOtherId });
      other.setValidators([
        Validators.required,
        Validators.maxLength(this.otherTextfieldMaxLength),
      ]);
    } else {
      if (
        this.homeownerForm.get('propertyType').value === this.defaultOtherId
      ) {
        this.homeownerForm.patchValue({ propertyType: '' });
      }
      other.patchValue('');
      other.clearValidators();
      other.updateValueAndValidity();
    }
  }

  checkFormValidity() {
    setTimeout((_) => {
      const onlyHomeowner =
        this.homeownerForm.value.onlyHomeowner === 'the_only_homeowner';
      const find_invalid = this.homeownersList.some((homeownerForm) => {
        return homeownerForm.invalid;
      });

      this.nextButtonState.emitActivateNext(
        this.homeownerForm.valid && (onlyHomeowner || !find_invalid)
      );
    });
  }

  updateFormValues() {
    this.propertyTypeRadioGroup =
      HOMEOWNER_INFO_MAP.addressFormRadioButtons.map(
        (item: { title: string; unique_name: string }) => {
          return {
            formControlName: 'propertyType',
            label: item.title,
            id: Number(item.unique_name),
            value: Number(item.unique_name),
          };
        }
      );
    this.radioGroup = HOMEOWNER_INFO_MAP.homeownerFormRadioButtons.map(
      (item: { title: string; unique_name: string }) => {
        return {
          formControlName: 'onlyHomeowner',
          label: item.title,
          id: item.unique_name,
          value: item.unique_name,
        };
      }
    );
    this.homeownerForm.patchValue({
      onlyHomeowner: this.previouslyFilledData?.ownership?.multipleOwners
        ? 'multiple_homeowners'
        : 'the_only_homeowner',
      propertyType:
        this.previouslyFilledData?.propertyType || this.defaultPropertyType,
      propertyTypeOtherText:
        this.previouslyFilledData?.propertyTypeOtherText || '',
    });
    if (this.previouslyFilledData?.ownership?.additionalOwners?.length) {
      this.homeownersList = [];
      this.previouslyFilledData?.ownership?.additionalOwners.map((owner) => {
        this.homeownersList.push(
          this.fb.group({
            firstName: [owner.firstName || '', Validators.required],
            lastName: [owner.lastName || '', Validators.required],
            phone: [
              owner.phone || '',
              [
                Validators.pattern('^[0-9]{10}$'),
                forbiddenPhoneValidator(FORBIDDEN_PHONES),
              ],
            ],
          })
        );
      });
    }
    if (this.previouslyFilledData?.propertyTypeOtherText) {
      this.switchOther(true);
    }
    this.trackHomeownersValidation();
    this.checkFormValidity();
  }

  prepopulateStepData(data: FormData) {
    if (data?.appointmentRequest?.property?.ownership) {
      this.previouslyFilledData = data.appointmentRequest.property;
    }
    if (data?.appointmentRequest?.project?.windowCount) {
      this.windowCount = Number(data.appointmentRequest.project?.windowCount);
    }
    this.updateFormValues();
  }

  makeLabel(template: string, index: number): string {
    return `${template} ${index + 1}`;
  }

  onAddHomeowner(): void {
    const newGroup = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [
        '',
        [
          Validators.pattern('^[0-9]{10}$'),
          forbiddenPhoneValidator(FORBIDDEN_PHONES),
        ],
      ],
    });

    const newGroupSub = newGroup.valueChanges.subscribe((value) => {
      this.checkFormValidity();
    });
    this.subscriptions.add(newGroupSub);
    this.homeownersList.push(newGroup);
    this.checkFormValidity();
  }

  trackHomeownersValidation() {
    this.homeownersList.forEach((homeownerForm) => {
      this.subscriptions.add(
        homeownerForm.valueChanges.subscribe((value) => {
          this.checkFormValidity();
        })
      );
    });
  }

  onRemoveHomeOwner(index: number): void {
    this.homeownersList.splice(index, 1);
    if (!this.homeownersList.length) {
      this.homeownerForm.patchValue({ onlyHomeowner: 'the_only_homeowner' });
    }
    this.checkFormValidity();
  }

  onClickNext = (): Observable<any> => {
    if (this.homeownerForm.valid) {
      return this.isEvent
        ? this.saveData(PAGES.ADDRESS)
        : this.saveData(NAVIGATION_MAP[PAGES.HOMEOWNER_LIST].nextButton.route);
    }
    this.homeownerForm.markAllAsTouched();
    return of(false);
  };

  onClickBack = (): Observable<any> => {
    const nextPage =
      this.windowCount === 0
        ? PAGES.WINDOW_COUNT
        : NAVIGATION_MAP[PAGES.HOMEOWNER_LIST].backButton.route;
    return this.homeownerForm.valid ? this.saveData(nextPage, true) : of(true);
  };

  saveData(nextPage, isBack?: boolean): Observable<any> {
    let homeowners = [];
    // check if user has homeowners
    if (this.homeownerForm.value.onlyHomeowner === 'multiple_homeowners') {
      // check all homeowners forms valid
      if (
        this.homeownersList.some((homeownerForm) => {
          if (homeownerForm.invalid) {
            homeownerForm.markAllAsTouched();
          }
          return homeownerForm.invalid;
        })
      ) {
        return of(isBack);
      }
      // mapping homeownersList for data for the backend
      this.homeownersList.forEach((homeownerForm) => {
        if (homeownerForm.valid) {
          homeowners.push(homeownerForm.value);
        } else {
          homeownerForm.markAllAsTouched();
        }
      });
      // double check validity for every form
      if (homeowners.length !== this.homeownersList.length) {
        return of(isBack);
      }
    }
    let data = {
      onlyHomeowner: this.homeownerForm.value.onlyHomeowner,
    };
    if (homeowners.length) {
      data['homeowners'] = homeowners;
    }

    const stepsData = { ...this.dataService.stepsData };
    stepsData.appointmentRequest.property.ownership.multipleOwners =
      this.homeownerForm.value.onlyHomeowner === 'multiple_homeowners';
    stepsData.appointmentRequest.property.ownership.additionalOwners =
      this.homeownersList.map((item) => item.value);
    stepsData.appointmentRequest.property.propertyType =
      this.homeownerForm.value.propertyType;
    stepsData.appointmentRequest.property.propertyTypeOtherText =
      this.homeownerForm.value.propertyTypeOtherText;
    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage;
    stepsData.sessionData = JSON.stringify(sessionData);

    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        return nextPage;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions) this.subscriptions.unsubscribe();
  }
}
